import { cardsEqual } from './card';
import { ROW_CAPACITY } from './constants';
import { RowPosition } from './enums';
import { Card, Placement, Row } from './types';

/**
 * Gets the capacity of a row by position
 */
export function getRowCapacity(position: RowPosition): number {
  return ROW_CAPACITY[position];
}

/**
 * Creates an empty row for the given position
 */
export function createEmptyRow(position: RowPosition): Row {
  return {
    position,
    cards: [],
  };
}

/**
 * Checks if a row is complete (has all cards)
 */
export function isRowComplete(row: Row): boolean {
  return row.cards.length >= getRowCapacity(row.position);
}

/**
 * Gets the number of empty slots in a row
 */
export function getEmptySlots(row: Row): number {
  return getRowCapacity(row.position) - row.cards.length;
}

/**
 * Adds a card to a row at the specified slot index
 * Returns a new row (immutable)
 */
export function addCardToRow(row: Row, card: Card, slotIndex?: number): Row {
  if (isRowComplete(row)) {
    throw new Error(`Row ${row.position} is already complete`);
  }

  const capacity = getRowCapacity(row.position);
  const effectiveSlotIndex = slotIndex ?? row.cards.length;

  if (effectiveSlotIndex < 0 || effectiveSlotIndex >= capacity) {
    throw new Error(`Invalid slot index ${effectiveSlotIndex} for row ${row.position}`);
  }

  // For simplicity, we append cards in order (slot index is for future positioning)
  const newCards = [...row.cards, card];

  return {
    ...row,
    cards: newCards,
  };
}

/**
 * Removes a card from a row
 * Returns a new row (immutable)
 */
export function removeCardFromRow(row: Row, card: Card): Row {
  const index = row.cards.findIndex((c) => cardsEqual(c, card));

  if (index === -1) {
    throw new Error(`Card not found in row ${row.position}`);
  }

  const newCards = [...row.cards];
  newCards.splice(index, 1);

  return {
    ...row,
    cards: newCards,
  };
}

/**
 * Checks if a placement is valid for a row
 */
export function isValidRowPlacement(row: Row, placement: Placement): boolean {
  if (placement.position !== row.position) {
    return false;
  }

  if (isRowComplete(row)) {
    return false;
  }

  const capacity = getRowCapacity(row.position);
  if (placement.slotIndex < 0 || placement.slotIndex >= capacity) {
    return false;
  }

  return true;
}

/**
 * Gets all cards in a row as an array
 */
export function getRowCards(row: Row): readonly Card[] {
  return row.cards;
}
